import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaCheck, FaClock, FaFileAlt, FaSpinner, FaTimes } from 'react-icons/fa';
import Card from '../../../components/Card/Card';
import { formatReportTypeDisplayName } from '../../../models/ReportTypeDesignHelper';
import './steps.css';
import { createLogger } from '../../../services/Debug';
function StepReportSelector({ onSelect, selected }) {
  const log = createLogger('StepReportSelector');
  const [reports, setReports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Load reports from localStorage on mount
  useEffect(() => {
    const loadReports = () => {
      setIsLoading(true);
      try {
        const storedReports = [];
        
        // Try to load from 'reports' key first
        const reportsData = localStorage.getItem('reports');
        if (reportsData) {
          try {
            const parsed = JSON.parse(reportsData);
            if (Array.isArray(parsed)) {
              parsed.forEach(report => {
                if (report && (report.text || report.content)) {
                  storedReports.push({
                    ...report,
                    id: report.id || `report-${Date.now()}-${Math.random()}`,
                    reportType: report.reportType || report.type || 'Rapport',
                    updatedAt: report.updatedAt || report.updated_at || report.date || new Date().toISOString(),
                    text: report.text || report.content || '',
                    content: report.text || report.content || ''
                  });
                }
              });
            }
          } catch (e) {
            log.error('Error parsing reports:', e);
          }
        }

        // If no reports found, scan localStorage for individual report entries
        if (storedReports.length === 0) {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('report') || key.includes('dictation'))) {
              try {
                const value = JSON.parse(localStorage.getItem(key));
                if (value && (value.text || value.content)) {
                  storedReports.push({
                    ...value,
                    id: value.id || key,
                    reportType: value.reportType || value.type || 'Rapport',
                    updatedAt: value.updatedAt || value.updated_at || value.date || new Date().toISOString(),
                    text: value.text || value.content || '',
                    content: value.text || value.content || ''
                  });
                }
              } catch (e) {
                // Skip non-JSON entries
              }
            }
          }
        }

        // Sort by date (most recent first)
        storedReports.sort((a, b) => {
          const dateA = new Date(a.updatedAt || 0);
          const dateB = new Date(b.updatedAt || 0);
          return dateB - dateA;
        });

        setReports(storedReports);
      } catch (error) {
        log.error('Error loading reports:', error);
        setReports([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadReports();
  }, []);

  // Filter reports based on search query
  const filteredReports = useMemo(() => {
    if (!searchQuery.trim()) return reports;
    
    const query = searchQuery.toLowerCase();
    return reports.filter(report => {
      const type = (report.reportType || '').toLowerCase();
      const text = (report.text || '').toLowerCase();
      return type.includes(query) || text.includes(query);
    });
  }, [reports, searchQuery]);

  // Group reports by date
  const groupedReports = useMemo(() => {
    const groups = {
      today: [],
      week: [],
      month: [],
      older: []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    filteredReports.forEach(report => {
      const date = new Date(report.updatedAt || 0);
      
      if (date >= today) {
        groups.today.push(report);
      } else if (date >= weekAgo) {
        groups.week.push(report);
      } else if (date >= monthAgo) {
        groups.month.push(report);
      } else {
        groups.older.push(report);
      }
    });

    return groups;
  }, [filteredReports]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `Il y a ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    } else if (diffHours < 48) {
      return 'Hier';
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const getExcerpt = (text, maxLength = 120) => {
    if (!text) return '';
    const cleaned = text.replace(/\s+/g, ' ').trim();
    return cleaned.length > maxLength 
      ? cleaned.substring(0, maxLength) + '...'
      : cleaned;
  };

  const handleReportSelect = (report) => {
    onSelect(report);
  };

  if (isLoading) {
    return (
      <div className="tbw-loading">
        <FaSpinner className="tbw-spinner" />
        <p>Chargement des rapports...</p>
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <div className="tbw-empty-state">
        <FaFileAlt className="tbw-empty-state-icon" />
        <div className="tbw-empty-state-title">Aucun rapport trouvé</div>
        <div className="tbw-empty-state-message">
          Créez d'abord des rapports dans l'application pour pouvoir générer des templates.
        </div>
      </div>
    );
  }

  return (
    <div className="step-report-selector">
      {/* Search Bar */}
      <div className="step-search-bar">
        <FaSearch className="step-search-icon" />
        <input
          type="text"
          placeholder="Rechercher..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="step-search-input"
          autoFocus
        />
        {searchQuery && (
          <button
            className="step-search-clear"
            onClick={() => setSearchQuery('')}
            aria-label="Effacer"
          >
            <FaTimes />
          </button>
        )}
      </div>

      {/* All Reports */}
      <div className="step-report-list">
        {/* Today */}
        {groupedReports.today.length > 0 && !searchQuery && (
          <div className="step-report-group">
            <h3 className="step-group-title">Aujourd'hui</h3>
            <div className="step-report-grid">
              {groupedReports.today.map(report => (
                <Card
                  key={report.id}
                  className={`step-report-card ${selected?.id === report.id ? 'selected' : ''}`}
                  onClick={() => handleReportSelect(report)}
                >
                  <div className="step-report-header">
                    <span className="step-report-type">{formatReportTypeDisplayName(report.reportType)}</span>
                    <span className="step-report-date">{formatDate(report.updatedAt)}</span>
                  </div>
                  <div className="step-report-excerpt">
                    {getExcerpt(report.text)}
                  </div>
                  {selected?.id === report.id && (
                    <div className="step-report-selected">
                      <FaCheck /> Sélectionné
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* This Week */}
        {groupedReports.week.length > 0 && !searchQuery && (
          <div className="step-report-group">
            <h3 className="step-group-title">Cette semaine</h3>
            <div className="step-report-grid">
              {groupedReports.week.map(report => (
                <Card
                  key={report.id}
                  className={`step-report-card ${selected?.id === report.id ? 'selected' : ''}`}
                  onClick={() => handleReportSelect(report)}
                >
                  <div className="step-report-header">
                    <span className="step-report-type">{formatReportTypeDisplayName(report.reportType)}</span>
                    <span className="step-report-date">{formatDate(report.updatedAt)}</span>
                  </div>
                  <div className="step-report-excerpt">
                    {getExcerpt(report.text)}
                  </div>
                  {selected?.id === report.id && (
                    <div className="step-report-selected">
                      <FaCheck /> Sélectionné
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Search Results or Other Reports */}
        {(searchQuery ? filteredReports : [...groupedReports.month, ...groupedReports.older]).length > 0 && (
          <div className="step-report-group">
            <h3 className="step-group-title">
              {searchQuery ? `Résultats (${filteredReports.length})` : 'Plus ancien'}
            </h3>
            <div className="step-report-grid">
              {(searchQuery ? filteredReports : [...groupedReports.month, ...groupedReports.older]).map(report => (
                <Card
                  key={report.id}
                  className={`step-report-card ${selected?.id === report.id ? 'selected' : ''}`}
                  onClick={() => handleReportSelect(report)}
                >
                  <div className="step-report-header">
                    <span className="step-report-type">{formatReportTypeDisplayName(report.reportType)}</span>
                    <span className="step-report-date">{formatDate(report.updatedAt)}</span>
                  </div>
                  <div className="step-report-excerpt">
                    {getExcerpt(report.text)}
                  </div>
                  {selected?.id === report.id && (
                    <div className="step-report-selected">
                      <FaCheck /> Sélectionné
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {searchQuery && filteredReports.length === 0 && (
          <div className="tbw-empty-state">
            <FaSearch className="tbw-empty-state-icon" />
            <div className="tbw-empty-state-title">Aucun résultat</div>
            <div className="tbw-empty-state-message">
              Aucun rapport ne correspond à votre recherche "{searchQuery}"
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StepReportSelector;